import { useId, useRef, useState, type ReactNode } from "react";
import type { EmailTemplate, Template } from "@/api/client";
import { ErrorBanner, ToolbarButton } from "@/components/ui";
import { useEmailTemplateChoices } from "@/features/email/choices";
import { Field, OtherProblems, inputClass, serverProblems, submitClass, useRevealProblems } from "@/components/form";
import {
  PROGRAM_FIELDS,
  previewNumber,
  validateProgramForm,
  type ProgramField,
  type ProgramFormValues,
} from "@/features/programs/programValues";

/** Every program field; a 422's details are shown on the field they name. */
export function ProgramForm({
  initial,
  floor = 1,
  templates,
  emailTemplates,
  numberLabel,
  numberHint,
  notes,
  isUnchanged,
  submitLabel,
  pendingLabel,
  pending,
  error,
  onEdit,
  onSubmit,
  onCancel,
  autoFocus = false,
}: {
  initial: ProgramFormValues;
  /** The lowest next number allowed: the saved one on edit. */
  floor?: number;
  /** Null when the user can't read templates, so there is nothing to choose from. */
  templates: Template[] | null;
  /** Null without certificate.send, so there is nothing to choose from. */
  emailTemplates: EmailTemplate[] | null;
  numberLabel: string;
  numberHint?: ReactNode;
  /** What the pending changes affect, given the values being edited. */
  notes?: (values: ProgramFormValues) => ReactNode;
  isUnchanged?: (values: ProgramFormValues) => boolean;
  submitLabel: string;
  pendingLabel: string;
  pending: boolean;
  error: unknown;
  onEdit?: () => void;
  onSubmit: (values: ProgramFormValues) => void;
  onCancel?: () => void;
  autoFocus?: boolean;
}) {
  const { archivedName } = useEmailTemplateChoices();
  const [values, setValues] = useState(initial);
  const clientErrors = validateProgramForm(values, floor);
  // A detail for a field this form doesn't show must still be seen, as an other problem.
  const shownFields = PROGRAM_FIELDS.filter(
    (f) => (f !== "template_id" || templates) && (f !== "email_template_id" || emailTemplates),
  );
  const server = serverProblems(error, shownFields);
  const paymentProblemId = useId();
  const formRef = useRef<HTMLFormElement>(null);
  const reveal = useRevealProblems<ProgramField>(formRef);
  const problem = (field: ProgramField) => reveal.shown(field, clientErrors[field]) ?? server.byField[field];
  const set = <K extends ProgramField>(field: K, value: ProgramFormValues[K]) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    onEdit?.();
  };
  const invalid = Object.keys(clientErrors).length > 0;
  const unchanged = isUnchanged?.(values) ?? false;
  const preview = previewNumber(values);

  const text = (
    field: Exclude<ProgramField, "requires_payment" | "email_template_id">,
    label: string,
    extra: { type?: string; step?: string; min?: number; max?: number; autoFocus?: boolean } = {},
    hint?: ReactNode,
  ) => (
    <Field label={label} problem={problem(field)} hint={hint}>
      {(props) => (
        <input {...props} {...extra} value={values[field]} onChange={(e) => set(field, e.target.value)} onBlur={() => reveal.touch(field)} className={inputClass} />
      )}
    </Field>
  );

  // A saved template without a published version stays selectable so the form shows it.
  const published = (templates ?? []).filter((t) => t.current_version);
  const savedTemplate = initial.template_id;
  const extra =
    savedTemplate && !published.some((t) => t.id === savedTemplate)
      ? [{ id: savedTemplate, label: `${templates?.find((t) => t.id === savedTemplate)?.name ?? savedTemplate} (no published version)` }]
      : [];

  return (
    <form
      ref={formRef}
      noValidate
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (invalid) {
          reveal.submitted();
          return;
        }
        if (!unchanged && !pending) onSubmit(values);
      }}
    >
      <div className="grid gap-3 md:grid-cols-3">
        {text("name", "Name", { autoFocus })}
        {text("program_type", "Type")}
        {text("induction_date", "Induction date", { type: "date" })}
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        {text("membership_no_prefix", "Number prefix")}
        {text("membership_no_width", "Number width", { type: "number", step: "1", min: 1, max: 20 })}
        {text("membership_no_next", "Next number", { type: "number", step: "1", min: floor }, numberHint)}
      </div>
      <p className="text-xs text-zinc-500">
        {preview ? `${numberLabel}: ${preview}` : "Membership numbers are the prefix followed by the number, zero-padded to the width."}
      </p>
      <div className="text-sm">
        <label className="flex items-center gap-2 text-zinc-700">
          <input
            type="checkbox"
            checked={values.requires_payment}
            onChange={(e) => set("requires_payment", e.target.checked)}
            aria-invalid={server.byField.requires_payment ? true : undefined}
            aria-describedby={server.byField.requires_payment ? paymentProblemId : undefined}
          />
          Requires payment
        </label>
        {server.byField.requires_payment ? (
          <p id={paymentProblemId} className="mt-1 text-xs text-red-700">
            {server.byField.requires_payment}
          </p>
        ) : null}
      </div>
      {templates ? (
        <Field label="Template" problem={problem("template_id")}>
          {(props) => (
            <select {...props} value={values.template_id} onChange={(e) => set("template_id", e.target.value)} onBlur={() => reveal.touch("template_id")} className={inputClass}>
              <option value="">No template</option>
              {extra.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
              {published.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
            </select>
          )}
        </Field>
      ) : null}
      {emailTemplates ? (
        <Field label="Email template" problem={problem("email_template_id")}>
          {(props) => (
            <select {...props} value={values.email_template_id} onChange={(e) => set("email_template_id", e.target.value)} onBlur={() => reveal.touch("email_template_id")} className={inputClass}>
              <option value="">System default</option>
              {initial.email_template_id && !emailTemplates.some((t) => t.id === initial.email_template_id) ? (
                <option value={initial.email_template_id}>{archivedName(initial.email_template_id)}</option>
              ) : null}
              {emailTemplates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
            </select>
          )}
        </Field>
      ) : null}
      {notes?.(values)}

      <ErrorBanner error={error} />
      <OtherProblems lines={server.other} />
      <div className="flex flex-wrap gap-2">
        <button type="submit" disabled={unchanged || pending} className={submitClass}>
          {pending ? pendingLabel : submitLabel}
        </button>
        {onCancel ? (
          <ToolbarButton disabled={pending} onClick={onCancel}>
            Cancel
          </ToolbarButton>
        ) : null}
      </div>
    </form>
  );
}
